import { IdentityUserAri, PlatformSiteAri } from '@atlassian/ari';
import type { EntryPointRef } from '@atlassian/entry-point-types/types';
import { homeRecentsListEntryPoint } from '@atlassian/home-recents/entrypoint';
import { loadEntryPoint } from 'react-relay';

import { getDefaultTabKey } from '@townsquare/hometab-saved-tab';
import { getAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { createResource } from '@townsquare/router/resources/createResource';
import type {
	RouteResource,
	RouterContext,
	RouterDataContext,
} from '@townsquare/router/routing-types';
import type { ResourceContext } from '@townsquare/router/types';

import { isRecentsTab, parseRecentsTab, RECENTS_TAB_QUERY_PARAM, type RecentsTab } from '../types';

export const FYP_FIRST = 40;
export const FULLSCREEN_FIRST = 100;

type HomeRecentsData = EntryPointRef<typeof homeRecentsListEntryPoint>;

/**
 * Undefined when this route has no recents list to preload — either it isn't a recents surface, or
 * the home tab will open on a tab other than worked on / viewed.
 */
const getRecentsActivityType = (
	routerContext: RouterContext | RouterDataContext,
): RecentsTab | undefined => {
	switch (routerContext.route.name) {
		case 'your-work': {
			const tabName = routerContext.match.params.tabName;

			if (tabName === 'recents') {
				return 'workedOn';
			} else if (tabName === 'viewed') {
				return 'viewed';
			} else {
				return undefined;
			}
		}
		case 'hometab': {
			const defaultTabKey = getDefaultTabKey();
			return isRecentsTab(defaultTabKey) ? defaultTabKey : undefined;
		}
		case 'recents':
			return parseRecentsTab(routerContext.query[RECENTS_TAB_QUERY_PARAM]);
		default:
			return undefined;
	}
};

export const getHomeRecentsResourceKey = (activityType: string): string =>
	`home-recents-resource-${activityType}`;

export const homeRecentsResource: RouteResource<HomeRecentsData | undefined> = createResource<
	HomeRecentsData | undefined
>({
	type: 'RECENTS',
	getKey: (routerContext) =>
		getHomeRecentsResourceKey(getRecentsActivityType(routerContext) ?? 'no-preload'),
	getData: async (routerContext, resourceContext) => {
		const isFullscreenPage =
			routerContext.route.name === 'recents' || routerContext.route.name === 'your-work';

		const activityType = getRecentsActivityType(routerContext);

		if (!activityType) {
			return;
		}

		const { user, collabContext } = resourceContext as ResourceContext;

		const cloudId = collabContext?.rovoWorkspace;

		if (!user?.accountId || !cloudId) {
			return;
		}

		const rootContainerCloudIds =
			collabContext?.collabContextScoping?.collabContextCloudIds ??
			collabContext?.orgCloudIds ??
			[];

		try {
			return loadEntryPoint({ getEnvironment: getAggRelayEnvironment }, homeRecentsListEntryPoint, {
				activityType,
				collabContextId: PlatformSiteAri.create({ siteId: cloudId }),
				rootContainerIds: rootContainerCloudIds.map((siteId) => PlatformSiteAri.create({ siteId })),
				currentUser: IdentityUserAri.create({ userId: user.accountId }),
				first: isFullscreenPage ? FULLSCREEN_FIRST : FYP_FIRST,
			});
		} catch {
			return;
		}
	},
});
